import type { ReactNode } from 'react';
import {
    Radar,
    RadarChart,
    PolarGrid,
    PolarAngleAxis,
    PolarRadiusAxis,
    ResponsiveContainer,
    Tooltip,
    Legend,
} from 'recharts';
import type { ChartSeries, RadarDatum, RadarDataLeadership } from './model';
import { createRadialLabelTickRenderer } from '@/pages/AssessmentLauncher/shared/utils';

// Calculate angles for each axis based on data length (rotate so 1st is at top)
const calculateAxisAngles = (dataLength: number) => {
    const angles: number[] = [];
    for (let i = 0; i < dataLength; i++) {
        angles.push((90 + (i * 360) / dataLength) % 360);
    }
    return angles;
};

export interface RadarChartCardProps {
    title?: string;
    data: (RadarDatum | RadarDataLeadership)[];
    maxValueData: number;
    showTeam?: boolean;
    variant?: 'default' | 'bare';
    height?: number;
    series?: ChartSeries[];
    footer?: ReactNode;
}

const RadarChartCard = ({
    title,
    data,
    maxValueData,
    showTeam,
    variant = 'default',
    height = 220,
    series,
    footer,
}: RadarChartCardProps) => {
    const wrapperClass =
        variant === 'bare'
            ? 'w-full h-full flex flex-col'
            : 'rounded-md border border-gray-200 bg-white p-4 flex flex-col';

    const axisAngles = calculateAxisAngles(data.length);
    const axisMaxValues = maxValueData;

    const getTickCount = (num: number): number => {
        if (num <= 0) {
            return 2;
        }

        const preferred = [4, 5, 7];
        for (const c of preferred) {
            if (num % c === 0) {
                return c + 1;
            }
        }
        return 5;
    };

    return (
        <div className={`${wrapperClass} justify-center`}>
            {title && title.trim() && <h4 className="mb-2 text-xs font-semibold text-gray-700">{title}</h4>}
            <div className="items-center justify-center">
                <ResponsiveContainer width="100%" height="100%" minHeight={height}>
                    <RadarChart data={data} margin={{ top: 30, right: 16, bottom: 16, left: 16 }}>
                        <PolarGrid stroke="var(--color-gray-200)" gridType="circle" />
                        <PolarAngleAxis
                            dataKey="axis"
                            tick={createRadialLabelTickRenderer(22, {
                                fontSize: 12,
                                fill: 'var(--color-neutral-500)',
                                fontWeight: 500,
                            })}
                        />
                        {!series && (
                            <Radar
                                name={showTeam ? 'Team' : 'Individual'}
                                dataKey={showTeam ? 'team' : 'individual'}
                                stroke="var(--color-blue-500)"
                                fill="var(--color-blue-500)"
                                fillOpacity={0.25}
                                strokeWidth={2}
                            />
                        )}
                        {series &&
                            series.map(s => (
                                <Radar
                                    key={s.key}
                                    name={s.name}
                                    dataKey={s.key}
                                    stroke={s.stroke}
                                    fill={s.fill || s.stroke}
                                    fillOpacity={s.fillOpacity ?? 0.25}
                                    strokeWidth={2}
                                />
                            ))}
                        {series && (
                            <Legend
                                verticalAlign="top"
                                wrapperStyle={{
                                    fontSize: 14,
                                    marginTop: -65,
                                    color: 'var(--color-gray-500)',
                                }}
                                formatter={value => <span style={{ color: 'var(--color-black)' }}>{value}</span>}
                            />
                        )}

                        {axisAngles.map((angle, index) => (
                            <PolarRadiusAxis
                                key={`pra-${index}`}
                                angle={angle}
                                domain={[0, axisMaxValues]}
                                tickCount={getTickCount(axisMaxValues)}
                                tickFormatter={(v: number) => (v === axisMaxValues ? String(axisMaxValues) : '')}
                                tick={{
                                    fontSize: 12,
                                    fill: 'var(--color-neutral-500)',
                                    fontWeight: 500,
                                    textAnchor: 'middle',
                                    dominantBaseline: 'middle',
                                    dx: Math.cos((angle * Math.PI) / 180) * 10,
                                    dy: -Math.sin((angle * Math.PI) / 180) * 10 - 5,
                                    transform: `rotate(${Math.PI / 2})`,
                                }}
                            />
                        ))}

                        <Tooltip formatter={(val: any, key: any) => [val, key]} contentStyle={{ fontSize: 16 }} />
                    </RadarChart>
                </ResponsiveContainer>
            </div>
            {footer && <div className="-mt-2 mb-[-55px] text-center text-base font-[550] text-gray-800">{footer}</div>}
        </div>
    );
};

export default RadarChartCard;
